import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Switch,
  TextField,
} from "@mui/material";
import { THEME } from "../constants";
import {
  dateBounds,
  formToRaw,
  formatField,
  isDirty,
  rawToForm,
  timestampField,
  validateForm,
  formatSince,
} from "../function";
import { CloseIcon, WarningIcon } from "./Icons";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#FAFBFF",
    fontFamily: THEME.font,
    "& fieldset": { borderColor: THEME.line },
    "&:hover fieldset": { borderColor: "#C7D0E0" },
    "&.Mui-focused fieldset": { borderColor: THEME.accent, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontFamily: THEME.font, fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: THEME.accent },
  "& .MuiFormHelperText-root": { fontFamily: THEME.font, marginLeft: "2px" },
};

// Grouped by field type, never by field name: a boolean or a read-only stamp
// takes a row of its own, and consecutive inputs of the same type pair up.
const rowsOf = (fields) => {
  const rows = [];

  for (let field of fields) {
    const pairable = field.type === "time" || field.type === "date";
    const last = rows[rows.length - 1];

    if (
      pairable &&
      last &&
      last.length === 1 &&
      last[0].type === field.type
    ) {
      last.push(field);
      continue;
    }

    rows.push([field]);
  }

  return rows;
};

function ScheduleEditModal({ open, target, connection, saving, error, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [baseline, setBaseline] = useState({});
  const [errors, setErrors] = useState({});

  // Which cell the form currently holds. Reseeding is keyed on this rather than
  // on render, so a parent re-render (a refresh landing, another cell saving)
  // cannot overwrite what the user is typing - while opening a different cell
  // always starts fresh, so a cancelled edit is dropped.
  const seededFor = useRef(null);

  const section = target?.section ?? null;
  // The device's connection: the page's current one, else the row's at opening.
  const deviceConnection = connection ?? target?.row?.connection ?? null;
  const cell = target?.cell ?? null;

  useEffect(() => {
    if (!open || !section) {
      seededFor.current = null;
      return;
    }

    if (seededFor.current === target.key) return;

    // Opens on `desired`, so a queued change can be seen and adjusted rather
    // than silently overwritten.
    const seeded = rawToForm(cell?.desired, section);

    seededFor.current = target.key;
    setForm(seeded);
    setBaseline(seeded);
    setErrors({});
  }, [open, target, cell, section]);

  if (!section) return null;

  const setValue = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleSave = () => {
    const found = validateForm(form, section, baseline);
    setErrors(found);

    if (Object.keys(found).length > 0) return;

    onSave(formToRaw(form, section, cell?.desired));
  };

  const dirty = isDirty(form, baseline);
  const stamp = timestampField(section);
  const reported = stamp ? formatField(stamp, cell?.current) : null;

  const renderField = (field) => {
    if (field.type === "boolean") {
      return (
        <div className="ds-switch-row" key={field.name}>
          <div>
            <div className="ds-switch-label">{field.label}</div>
            <div className="ds-switch-sub">
              {form[field.name]
                ? "The device will run this schedule."
                : "The device will ignore this schedule."}
            </div>
          </div>
          <Switch
            checked={Boolean(form[field.name])}
            onChange={(event) => setValue(field.name, event.target.checked)}
            sx={{
              "& .Mui-checked": { color: section.accent },
              "& .Mui-checked + .MuiSwitch-track": {
                backgroundColor: section.accent,
              },
            }}
          />
        </div>
      );
    }

    if (field.type === "timestamp") {
      return (
        <div className="ds-readonly-row" key={field.name}>
          <span>{field.label} (reported by the device)</span>
          <span>{reported || "Never"}</span>
        </div>
      );
    }

    const bounds = field.type === "date" ? dateBounds(field) : {};

    return (
      <TextField
        key={field.name}
        label={field.label}
        type={field.type === "date" ? "date" : "time"}
        value={form[field.name] ?? ""}
        onChange={(event) => setValue(field.name, event.target.value)}
        error={Boolean(errors[field.name])}
        helperText={errors[field.name] || " "}
        size="small"
        fullWidth
        sx={inputSx}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: bounds.min, max: bounds.max }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <div className="ds-modal-head">
        <div
          className="ds-modal-badge"
          style={{ backgroundColor: section.tint, color: section.accent }}
        >
          <WarningIcon size={20} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="ds-modal-title">{section.label}</div>
          <div className="ds-modal-subtitle">
            {target.row.name || "Unnamed device"} &middot; {target.row.deviceId}
          </div>
        </div>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent sx={{ padding: "16px 24px 8px" }}>
        <div className="ds-modal-hint">{section.hint}</div>

        {/* An offline device takes the change only once it reconnects - said
            before saving, so the Pending badge afterwards is no surprise. */}
        {deviceConnection?.online === false && (
          <div className="ds-warn-strip ds-offline-strip">
            <WarningIcon />
            <span>
              {deviceConnection.since
                ? `This device has been offline since ${formatSince(deviceConnection.since)}.`
                : "This device is offline."}{" "}
              You can still save - the schedule will be applied when it comes back online.
            </span>
          </div>
        )}

        {cell?.pending && (
          <div className="ds-warn-strip">
            <WarningIcon />
            <span>
              A change is already queued for this device and has not been applied
              yet. These are the queued values - saving replaces them.
            </span>
          </div>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              marginTop: "10px",
              borderRadius: "12px",
              fontFamily: THEME.font,
            }}
          >
            {error}
          </Alert>
        )}

        <div className="ds-fields">
          {rowsOf(section.fields).map((row, index) => (
            <div
              className={row.length > 1 ? "ds-field-row" : ""}
              key={row.map((field) => field.name).join("-") || index}
            >
              {row.map(renderField)}
            </div>
          ))}
        </div>
      </DialogContent>

      <DialogActions
        sx={{ padding: "12px 24px 18px", borderTop: `1px solid ${THEME.line}` }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            fontFamily: THEME.font,
            fontWeight: 600,
            textTransform: "none",
            color: THEME.muted,
            borderRadius: "10px",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disableElevation
          // Nothing changed means nothing to send.
          disabled={saving || !dirty}
          startIcon={
            saving ? <CircularProgress size={15} sx={{ color: "#FFFFFF" }} /> : null
          }
          sx={{
            fontFamily: THEME.font,
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "10px",
            padding: "7px 20px",
            backgroundColor: section.accent,
            "&:hover": { backgroundColor: section.accent, filter: "brightness(0.92)" },
          }}
        >
          {saving ? "Saving" : "Save schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ScheduleEditModal;
